import { api, apiPatch } from '@/api/client'

export type IncidentStatus = 'new' | 'confirmed' | 'rejected'

export interface PosIncident {
  id: number
  terminal_id: number
  terminal_num?: number | null
  trassir_server_id?: number | null
  trassir_channel_guid?: string | null
  trassir_channel_name?: string | null
  server_id: number
  op_id: string | null
  type: string
  type_label: string
  kind: string | null
  kind_label: string | null
  severity: 'high' | 'medium' | 'low' | null
  text: string | null
  price: number | null
  quantity: number | null
  position: string | null
  event_ts_us: number | null
  event_at: string | null
  status: IncidentStatus
  comment: string | null
  damage: number | null
  reviewed_by: number | null
  reviewed_at: string | null
}

export interface PosIncidentContextEvent {
  id: number
  op_id: string | null
  type: string
  type_label: string
  kind: string | null
  text: string | null
  price: number | null
  quantity: number | null
  position: string | null
  event_ts_us: number | null
  is_alert: boolean
  is_focus: boolean
}

export interface BarcodeStatItem {
  text: string
  count: number
}

export interface IncidentKindOption {
  value: string
  label: string
}

function todayMsk(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

export { todayMsk }

export async function fetchIncidentKinds(): Promise<IncidentKindOption[]> {
  const data = await api<{ kinds?: IncidentKindOption[] }>('/api/trassir/pos-incident-kinds')
  return data.kinds || []
}

export async function fetchTerminalPosIncidents(
  terminalId: number,
  opts?: {
    day?: string
    kind?: string
    status?: IncidentStatus | 'all'
    text?: string
    limit?: number
  },
): Promise<{ day: string; incidents: PosIncident[] }> {
  const q = new URLSearchParams()
  q.set('day', opts?.day || todayMsk())
  if (opts?.kind) q.set('kind', opts.kind)
  if (opts?.status && opts.status !== 'all') q.set('status', opts.status)
  if (opts?.text) q.set('text', opts.text)
  if (opts?.limit) q.set('limit', String(opts.limit))
  return api(`/api/trassir/terminals/${terminalId}/pos-incidents?${q}`)
}

export async function fetchBarcodeStats(
  terminalId: number,
  opts?: { day?: string; limit?: number },
): Promise<{ day: string; items: BarcodeStatItem[] }> {
  const q = new URLSearchParams()
  q.set('day', opts?.day || todayMsk())
  if (opts?.limit) q.set('limit', String(opts.limit))
  return api(`/api/trassir/terminals/${terminalId}/pos-incidents/barcode-stats?${q}`)
}

export async function fetchIncidentContext(
  terminalId: number,
  eventId: number,
): Promise<{ focus_id: number; mode: string; events: PosIncidentContextEvent[] }> {
  return api(`/api/trassir/terminals/${terminalId}/pos-incidents/${eventId}/context`)
}

export async function patchIncidentStatus(
  terminalId: number,
  eventId: number,
  body: { status: IncidentStatus; comment?: string | null; damage?: number | null },
): Promise<PosIncident> {
  const data = await apiPatch<{ incident: PosIncident }>(
    `/api/trassir/terminals/${terminalId}/pos-incidents/${eventId}`,
    body,
  )
  return data.incident
}
